import { useState, useEffect, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import LegalLayout from './LegalLayout'
import {
  Trash2,
  CheckCircle2,
  Shield,
  Search,
  ExternalLink,
  AlertCircle,
  Mail,
  Phone,
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'


interface DeletionStatusResult {
  confirmationCode: string
  source: string
  status: string
  requestedAt: string
  completedAt?: string
  details?: string
  email?: string
  phone?: string
}

export default function DataDeletionPage() {
  const [searchParams] = useSearchParams()
  const initialCode = searchParams.get('code') || ''

  // Form states
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submittedResult, setSubmittedResult] = useState<{
    confirmationCode: string
    status: string
    url: string
    requestedAt: string
  } | null>(null)

  // Status check states
  const [checkCode, setCheckCode] = useState(initialCode)
  const [checking, setChecking] = useState(false)
  const [statusResult, setStatusResult] = useState<DeletionStatusResult | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)

  useEffect(() => {
    if (initialCode) {
      handleCheckStatus(initialCode)
    }
  }, [initialCode])

  const handleSubmitRequest = async (e: FormEvent) => {
    e.preventDefault()

    if (!email.trim() && !phone.trim()) {
      toast.error('Ingresa al menos un correo electrónico o número de teléfono')
      return
    }

    try {
      setSubmitting(true)
      const { data } = await api.post<{
        success: boolean
        data: {
          confirmationCode: string
          status: string
          url: string
          requestedAt: string
        }
      }>('/data-deletion/request', {
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        reason: reason.trim() || undefined,
      })

      if (data.success && data.data) {
        setSubmittedResult(data.data)
        toast.success('Solicitud de eliminación registrada satisfactoriamente')
      }
    } catch (error: any) {
      console.error('Error submitting data deletion request:', error)
      toast.error(
        error?.response?.data?.message ||
          'Ocurrió un error al procesar tu solicitud. Por favor intenta de nuevo.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleCheckStatus = async (codeToQuery?: string) => {
    const code = (codeToQuery || checkCode).trim()
    if (!code) {
      toast.error('Ingresa un código de confirmación válido')
      return
    }

    try {
      setChecking(true)
      setStatusError(null)
      const { data } = await api.get<{
        success: boolean
        data: DeletionStatusResult
      }>(`/data-deletion/status/${encodeURIComponent(code)}`)

      if (data.success && data.data) {
        setStatusResult(data.data)
      } else {
        setStatusError('No se encontró información para el código provisto.')
      }
    } catch (error: any) {
      console.error('Error fetching deletion status:', error)
      setStatusResult(null)
      setStatusError(
        error?.response?.data?.message ||
          'Código no encontrado o no válido. Verifica el identificador ingresado.'
      )
    } finally {
      setChecking(false)
    }
  }

  return (
    <LegalLayout
      title="Instrucciones y Solicitud de Eliminación de Datos"
      subtitle="Guía oficial conforme a los requisitos de Meta Platforms y solicitud de supresión de datos de usuario"
      lastUpdated="15 de octubre de 2026"
    >
      <div className="space-y-10 text-slate-700 leading-relaxed text-sm sm:text-base">
        {/* Intro */}
        <section className="space-y-3">
          <p>
            En cumplimiento con las directrices de la plataforma <strong>Meta for Developers</strong> y las normativas internacionales de protección de datos (GDPR / LGPD), <strong>IridiaChat</strong> garantiza que cualquier usuario final o empresa asociada puede solicitar la desvinculación y eliminación total de sus datos personales y de sus registros en cualquier momento.
          </p>
        </section>

        {/* Section 1: Desvinculación desde Facebook */}
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-slate-900 font-bold text-base sm:text-lg">
            <Shield className="w-5 h-5 text-emerald-700" />
            <span>Método 1: Desvincular IridiaChat desde tu cuenta de Facebook</span>
          </div>
          <p className="text-slate-600 text-sm">
            Si conectaste tu cuenta de WhatsApp Business mediante el Registro Incrustado (<em>Embedded Signup</em>) con Facebook Login y deseas revocar los permisos directamente desde Meta:
          </p>
          <ol className="list-decimal pl-5 space-y-2 text-slate-700 text-sm sm:text-base">
            <li>
              Inicia sesión en tu cuenta de Facebook y dirígete a{' '}
              <a
                href="https://www.facebook.com/settings?tab=applications"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 font-semibold underline inline-flex items-center gap-1"
              >
                Configuración de la cuenta &gt; Apps y sitios web
                <ExternalLink className="w-3.5 h-3.5" />
              </a>.
            </li>
            <li>En la lista de aplicaciones activas, localiza <strong>IridiaChat</strong>.</li>
            <li>Haz clic en el botón <strong>Eliminar</strong> o <em>Revocar acceso</em>.</li>
            <li>
              Marca la casilla si deseas que Facebook notifique a IridiaChat para que se elimine todo el contenido compartido.
            </li>
            <li>
              Meta enviará una notificación automática a nuestro servidor mediante el webhook de devolución de llamada de eliminación de datos (<em>Data Deletion Callback</em>) y tus credenciales serán purgadas inmediatamente.
            </li>
          </ol>
        </section>

        {/* Section 2: Eliminación desde el Panel */}
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-slate-900 font-bold text-base sm:text-lg">
            <Trash2 className="w-5 h-5 text-emerald-700" />
            <span>Método 2: Eliminación directa desde el Panel de IridiaChat</span>
          </div>
          <p className="text-slate-600 text-sm">
            Si eres un usuario registrado (administrador o miembro del equipo de un negocio) en la plataforma:
          </p>
          <ol className="list-decimal pl-5 space-y-2 text-slate-700 text-sm sm:text-base">
            <li>Inicia sesión en <a href="/login" className="text-emerald-700 font-semibold underline">iridia-chat.juntaclick.com/login</a>.</li>
            <li>Ve a la sección <strong>Mi Perfil</strong> desde la barra de navegación o el menú superior.</li>
            <li>Baja hasta la sección <strong>Zona de Peligro &gt; Eliminar Cuenta</strong>.</li>
            <li>Introduce tu contraseña para confirmar tu identidad y autorizar la supresión.</li>
            <li>Tu usuario, credenciales, membresías y sesiones activas quedarán eliminados permanentemente.</li>
          </ol>
        </section>

        {/* Section 3: Formulario Público de Eliminación */}
        <section className="bg-white border-2 border-emerald-500/30 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold mb-2">
              Formulario en Línea
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
              Solicitar Eliminación de Datos Inmediata
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              Si eres un cliente final que ha interactuado con un bot de WhatsApp operado a través de nuestra plataforma, puedes solicitar el borrado de tus datos ingresando tu identificador:
            </p>
          </div>

          {submittedResult ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-emerald-900">
                    ¡Solicitud de Eliminación Registrada!
                  </h3>
                  <p className="text-xs sm:text-sm text-emerald-800">
                    Tu solicitud ha sido procesada de acuerdo con las normativas de Meta y políticas de privacidad.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-emerald-200 rounded-lg p-4 space-y-2">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                  Código de Confirmación Oficial (Meta Compliance Code):
                </div>
                <div className="font-mono text-lg sm:text-xl font-bold text-slate-900 select-all">
                  {submittedResult.confirmationCode}
                </div>
                <div className="text-xs text-slate-500">
                  Guarda este código para verificar el estado de supresión de datos.
                </div>
              </div>

              <div className="text-xs sm:text-sm text-slate-600">
                <strong>Enlace de Seguimiento:</strong>{' '}
                <a
                  href={submittedResult.url}
                  className="text-emerald-700 font-mono underline break-all"
                >
                  {submittedResult.url}
                </a>
              </div>

              <button
                type="button"
                onClick={() => setSubmittedResult(null)}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline"
              >
                ← Enviar otra solicitud
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Correo Electrónico (opcional si ingresas teléfono)
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Número de Teléfono / WhatsApp (con código de país)
                  </label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+58 412 1234567"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Motivo o comentario adicional (opcional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: Deseo retirar mi consentimiento y purgar mi historial de mensajes."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{submitting ? 'Registrando...' : 'Solicitar Eliminación de Mis Datos'}</span>
                </button>
              </div>
            </form>
          )}
        </section>

        {/* Section 4: Consultar Estado de Solicitud */}
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-slate-900 font-bold text-base sm:text-lg">
            <Search className="w-5 h-5 text-emerald-700" />
            <span>Consultar Estado de una Solicitud de Eliminación</span>
          </div>
          <p className="text-slate-600 text-sm">
            Si ya enviaste una solicitud o recibiste un código de confirmación de Meta, puedes verificar el estado actual aquí:
          </p>

          <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
            <input
              type="text"
              value={checkCode}
              onChange={(e) => setCheckCode(e.target.value)}
              placeholder="Ej: DEL-M4KL9Z-1A2B3C"
              className="flex-1 px-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => handleCheckStatus()}
              disabled={checking}
              className="inline-flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              <span>{checking ? 'Consultando...' : 'Consultar'}</span>
            </button>
          </div>

          {statusError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{statusError}</span>
            </div>
          )}

          {statusResult && (
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 mt-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="font-mono text-sm font-bold text-slate-900">
                  {statusResult.confirmationCode}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {statusResult.status}
                </span>
              </div>
              <div className="text-xs sm:text-sm text-slate-600 space-y-1">
                <div>
                  <strong>Fecha de Solicitud:</strong>{' '}
                  {new Date(statusResult.requestedAt).toLocaleString()}
                </div>
                {statusResult.completedAt && (
                  <div>
                    <strong>Fecha de Procesamiento:</strong>{' '}
                    {new Date(statusResult.completedAt).toLocaleString()}
                  </div>
                )}
                <div className="mt-2 text-slate-700 bg-slate-50 p-2.5 rounded border border-slate-200 text-xs">
                  {statusResult.details || 'Registros eliminados y desvinculados exitosamente.'}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Section 5: Contacto */}
        <section className="space-y-3 pt-4 border-t border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Asistencia y Contacto del Responsable</h2>
          <p className="text-sm text-slate-600">
            Para cualquier inquietud sobre la eliminación de tus datos o asistencia técnica directa, puedes contactar al equipo de privacidad de <strong>INVERSIONES MERCADO NARANJA, C.A.</strong> (RIF J-502314539) a través de{' '}
            <a href="mailto:soporte@iridiachat.com" className="text-emerald-700 font-semibold underline">
              soporte@iridiachat.com
            </a>{' '}
            o{' '}
            <a href="mailto:privacidad@iridiachat.com" className="text-emerald-700 font-semibold underline">
              privacidad@iridiachat.com
            </a>.
          </p>
        </section>
      </div>
    </LegalLayout>
  )
}
